import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useCallback, useRef, useState } from 'react'
import { z } from 'zod'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import TitleRow from '@/components/ui/TitleRow'
import IndicesChartCard from '@/features/market/components/IndicesChartCard'
import IndicesTable from '@/features/market/components/IndicesTable'
import { IndicesBoardSkeleton } from '@/screens/market/components/IndicesBoardSkeleton'
import { useReducedMotion } from '@/lib/motion/use-reduced-motion'
import { loadIndices } from '@/screens/market/IndicesScreen.loader'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title="Indices" />

/**
 * No search param picks anything on this page (the selected row is local
 * `useState`, seeded to NEPSE), so — unlike the other five families this pass
 * converts — the first load is a plain suspense page, the same shape
 * `/market` and `/stocks/$symbol` already take.
 */
function IndicesPending() {
  return (
    <ScreenLayout pending pendingLabel="Loading the indices…" header={HEADER} pageTitle="Indices">
      <IndicesBoardSkeleton />
    </ScreenLayout>
  )
}

/** Kept light — RouteErrorPage plus the same header the pending page shows —
 *  so the frame does not change between the two: `errorComponent`s ship in
 *  the entry chunk, and a chunk that cannot load offline reloads the app. */
function IndicesError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Couldn't load the indices" />
}

/**
 * `?index=` opens the page on one index's chart: a sector's row on Market · Sectors links here with
 * that sector's own index key. The key is whatever the indices answer carries; one it does not hold
 * falls back to NEPSE.
 */
const indicesSearchSchema = z.object({ index: z.string().min(1).optional() })

export const Route = createFileRoute('/_app/market/indices')({
  validateSearch: indicesSearchSchema,
  loader: loadIndices,
  pendingComponent: IndicesPending,
  errorComponent: IndicesError,
  component: IndicesScreen,
})

/**
 * Market indices — the four NEPSE-wide readings, charted one at a time.
 *
 * Nothing above the chart: the section tab strip names the page, so there is no pill strip, no
 * More popover and no subtitle paragraph.
 *
 * The selection lives here rather than in either child because both need it —
 * the table sets it and the chart reads it.
 */
function IndicesScreen() {
  // The chart card and the table below both call `useIndices()` for themselves and dedupe on the
  // key; under the loader neither ever shows its own skeleton.
  const { index } = Route.useSearch()
  const [picked, setPicked] = useState<string | null>(null)
  const selectedId = picked ?? index?.toLowerCase() ?? 'nepse'
  const reducedMotion = useReducedMotion()
  const chartRef = useRef<HTMLDivElement>(null)

  // The table sits below the chart, so a pick made far down the page changes
  // a chart that is off-screen. Bring it back only when its top is not
  // already clear of the sticky nav (the wrapper's scroll-margin keeps the
  // landing spot below it); focus stays on the row's button.
  const selectIndex = useCallback(
    (id: string) => {
      setPicked(id)
      const chart = chartRef.current
      if (chart === null || typeof chart.scrollIntoView !== 'function') return
      const clearance = document.querySelector('header')?.getBoundingClientRect().bottom ?? 0
      if (chart.getBoundingClientRect().top >= clearance) return
      chart.scrollIntoView({ block: 'start', behavior: reducedMotion ? 'auto' : 'smooth' })
    },
    [reducedMotion],
  )

  return (
    <ScreenLayout header={HEADER} pageTitle="Indices">
      <div ref={chartRef} className="scroll-mt-[calc(var(--header-h)+var(--space-4))]">
        <IndicesChartCard indexId={selectedId} />
      </div>

      <IndicesTable selectedId={selectedId} onSelect={selectIndex} />
    </ScreenLayout>
  )
}
